/**
 * LivePreviewPanel — Real-time price preview during Run a Sale.
 *
 * Shows computed sale price, original price (strikethrough),
 * savings amount, and date range. Updates as the merchant types.
 *
 * Desktop: always visible (right 40% of modal).
 * Mobile: collapsed "Preview" bar, expands on tap.
 */

import { useState } from "react";
import { cn } from "@/lib/utils";
import { formatPrice } from "@/lib/format/currency";
import { ChevronDown, ChevronUp } from "lucide-react";

interface LivePreviewPanelProps {
  basePrice: number | null;
  computedPrice: number | null;
  currency: string;
  validFrom: string;
  validUpto: string;
}

function formatDateRange(from: string, to: string): string {
  if (!from) return "";
  const fmt = (d: string) => {
    try {
      const date = new Date(d);
      if (isNaN(date.getTime())) return d;
      const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      return `${months[date.getMonth()]} ${date.getDate()}`;
    } catch { return d; }
  };
  const year = from ? new Date(from).getFullYear() : "";
  if (!to) return `From ${fmt(from)}, ${year}`;
  return `${fmt(from)} → ${fmt(to)}, ${year}`;
}

function PreviewContent({
  basePrice, computedPrice, currency, validFrom, validUpto,
}: LivePreviewPanelProps) {
  const savings = basePrice !== null && computedPrice !== null
    ? Math.round((basePrice - computedPrice) * 100) / 100
    : null;
  const hasSavings = savings !== null && savings > 0;

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground">
        Your customer sees
      </p>
      <p className="text-3xl font-semibold tabular-nums text-foreground">
        {formatPrice(computedPrice, currency)}
      </p>
      {hasSavings && basePrice !== null && (
        <>
          <p className="text-sm text-muted-foreground line-through tabular-nums">
            {formatPrice(basePrice, currency)}
          </p>
          <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
            Saving {formatPrice(savings, currency)}
          </p>
        </>
      )}
      {(validFrom || validUpto) && (
        <p className="text-xs text-muted-foreground mt-2">
          {formatDateRange(validFrom, validUpto)}
        </p>
      )}
    </div>
  );
}

export function LivePreviewPanel(props: LivePreviewPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      {/* Desktop: always visible */}
      <div
        className={cn(
          "hidden md:block p-4 rounded-md",
          "bg-emerald-50/40 dark:bg-emerald-950/20"
        )}
        aria-live="polite"
      >
        <PreviewContent {...props} />
      </div>

      {/* Mobile: collapsible bar */}
      <div className="md:hidden" aria-live="polite">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className={cn(
            "w-full flex items-center justify-between px-4 py-3 rounded",
            "bg-emerald-50/40 dark:bg-emerald-950/20",
            "text-sm font-medium text-foreground"
          )}
        >
          <span>
            Preview: {formatPrice(props.computedPrice, props.currency)}
          </span>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
        {isExpanded && (
          <div className="px-4 pt-2 pb-3 rounded-b bg-emerald-50/40 dark:bg-emerald-950/20 -mt-2">
            <PreviewContent {...props} />
          </div>
        )}
      </div>
    </>
  );
}
